/**
 * Compute a trial rectangle from two center-line points and a width.
 * Uses haversine-based destination calculation for geographic accuracy.
 *
 * The rectangle is oriented along the A→B direction, with width extending
 * equally on both sides of the center-line.
 */

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const EARTH_RADIUS_KM = 6371.0088;

/** Bearing in degrees from point A to point B */
function bearing(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number }
): number {
  const dLon = (b.lng - a.lng) * DEG;
  const lat1 = a.lat * DEG;
  const lat2 = b.lat * DEG;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * RAD) + 360) % 360;
}

/** Destination point given start, distance in km, and bearing in degrees */
function destination(
  origin: { lng: number; lat: number },
  distanceKm: number,
  bearingDeg: number
): [number, number] {
  const lat1 = origin.lat * DEG;
  const lon1 = origin.lng * DEG;
  const brng = bearingDeg * DEG;
  const angDist = distanceKm / EARTH_RADIUS_KM;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angDist) +
      Math.cos(lat1) * Math.sin(angDist) * Math.cos(brng)
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(angDist) * Math.cos(lat1),
      Math.cos(angDist) - Math.sin(lat1) * Math.sin(lat2)
    );

  return [lon2 * RAD, lat2 * RAD]; // [lng, lat]
}

/**
 * Compute the GeoJSON Polygon coordinates for a trial rectangle.
 *
 * @param pointA Center-line start { lng, lat } in WGS84
 * @param pointB Center-line end { lng, lat } in WGS84
 * @param widthMeters Width in meters perpendicular to center-line
 * @returns GeoJSON Polygon coordinate ring [[lng, lat], ...]
 */
export function computeTrialRectangle(
  pointA: { lng: number; lat: number },
  pointB: { lng: number; lat: number },
  widthMeters: number
): number[][] {
  const fwd = bearing(pointA, pointB);
  const perpLeft = fwd - 90;
  const perpRight = fwd + 90;
  const halfWidthKm = widthMeters / 1000 / 2;

  const c1 = destination(pointA, halfWidthKm, perpLeft);
  const c2 = destination(pointA, halfWidthKm, perpRight);
  const c3 = destination(pointB, halfWidthKm, perpRight);
  const c4 = destination(pointB, halfWidthKm, perpLeft);

  return [c1, c2, c3, c4, c1]; // closed ring
}
